import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, NoteStatus, NoteType, SourceDataPriority } from "@/api/dtos";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Theme, Tooltip, TooltipProps, darken, lighten, styled, tooltipClasses, Link as MuiLink } from "@mui/material";
import { grey } from "@mui/material/colors";
import { DateTime } from "luxon";
import { ComponentType, FC, PropsWithChildren, memo, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { IStatusListDataObject, IStatusListItemData, IStatusListStatsComponentProps } from "./StatusList";
import noteTypeToText from "@/utilities/configuration/noteTypeToText";

export const StatusListStatsBox = styled(Box)(({ theme }) => ({
    top: 0, 
    bottom: 0, 
    height: theme.spacing(6), 
    display: 'flex', 
    marginRight: theme.spacing(0)
}));

export const StatusListStatBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(15),
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
}));

export const StatusListItemTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        minWidth: theme.spacing(27.5),
        maxWidth: 'unset',
        fontSize: '1rem',
        fontWeight: 'normal',
        padding: theme.spacing(2),
        backgroundColor: grey[700]
    },
}));

export const StatusListItemButtonBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between',
}));

export const StatusListItemLoadingButton = styled(LoadingButton)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));

export const StatusListItemIconBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    width: theme.spacing(4),
    justifyContent: 'center',
    marginRight: theme.spacing(1)
}));

export const StatusListItemButtonTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        minWidth: theme.spacing(35),
        fontSize: '1rem',
        fontWeight: 'normal',
        padding: theme.spacing(2),
        backgroundColor: grey[700]
    },
}));

export const StatusListItemTooltipStats = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),

    '& > span': {
        whiteSpace: 'nowrap'
    }
}));

export const StatusListNoteTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        minWidth: theme.spacing(100),
        fontSize: '1rem',
        fontWeight: 'normal',
        padding: theme.spacing(2),
        backgroundColor: grey[700]
    },
}));

export const StatusListNoteTable = styled('table')(({ theme }) => ({ 
    width: '100%',
    borderCollapse: 'collapse',

    '& th': {
        border: `1px solid ${grey[500]}`,
        textAlign: 'center',
        padding: theme.spacing(0.5)
    },
    '& td': {
        border: `1px solid ${grey[500]}`,
        textAlign: 'center',
        padding: theme.spacing(0.5)
    },

    '& tbody th': {
        textAlign: 'left'
    },
    '& tfoot th:first-of-type': {
        textAlign: 'left'
    }
}));

const StyledFontAwesomeIcon = styled(FontAwesomeIcon) (() => ({
}));


export const getFormCompleteColor = (theme: Theme, complete?: number) => {
    const colorFn = theme.palette.mode === 'dark' ? darken : lighten;

    if (complete === undefined) {
        return colorFn(grey[600], 0.5);
    }

    if (complete >= 100) {
        return colorFn(theme.palette.success.main, 0.5);
    }

    if (complete <= 0) {
        return colorFn(theme.palette.error.main, 0.5);
    }

    return `color-mix(in srgb, ${colorFn(theme.palette.warning.main, 0.5)} ${(complete)}%, ${colorFn(theme.palette.error.main, 0.5)})`
};


export interface IStatusListUpdateButtonProps<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    itemData: Omit<IStatusListItemData<TData, TAdditional>, 'render'> & { render: Omit<IStatusListItemData<TData, TAdditional>['render'], 'stats'>};
    theme: Theme;
}


export const StatusListUpdateButton = <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
    itemData, 
    theme
}: IStatusListUpdateButtonProps<TData, TAdditional>) => {
    const [processing, setProcessing] = useState(false);

    return (
        <StatusListItemButtonTooltip
            placement="left"
            title={
                <>
                    <strong>Update Status</strong>
                    <StatusListItemTooltipStats>
                        <span>
                            Last Update
                        </span>
                        <span>
                            {itemData.status?.updatedDate ? DateTime.fromISO(itemData.status.updatedDate).toFormat('dd/MM/yyyy HH:mm:ss') : 'No Data'}
                        </span>
                    </StatusListItemTooltipStats>
                </>
            }
        >
            <StatusListItemButtonBox>
                <StatusListItemLoadingButton loading={itemData.status?.updating || processing} onClick={async () => {
                    setProcessing(true);

                    try {
                        await itemData.render.queueUpdate?.();
                    }
                    finally
                    {
                        setProcessing(false);
                    }
                }}>
                    <FontAwesomeIcon icon={AppSettings.icons.refresh} fixedWidth />
                </StatusListItemLoadingButton>
            </StatusListItemButtonBox>
        </StatusListItemButtonTooltip>
    )
}

export interface IStatusListConfigurationButtonProps<TData extends IStatusListDataObject = any, TAdditional extends object = any> {
    itemData: Omit<IStatusListItemData<TData, TAdditional>, 'render'> & { render: Omit<IStatusListItemData<TData, TAdditional>['render'], 'stats'>};
    theme: Theme;
}

export const StatusListConfigurationButton = <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
    itemData, 
    theme
}: IStatusListConfigurationButtonProps<TData, TAdditional>) => {
    const location = useLocation();

    return (
        <StatusListItemButtonTooltip
            placement="left"
            title={
                <>
                    <strong>Configure</strong>
                </>
            }
        >
            <StatusListItemButtonBox>
                <StatusListItemLoadingButton 
                    //@ts-ignore-next-line
                    component={Link} 
                    to={itemData.render.configureLink!} 
                    state={{ from: location }}
                >
                    <FontAwesomeIcon icon={AppSettings.icons.configuration} fixedWidth />
                </StatusListItemLoadingButton>
            </StatusListItemButtonBox>
        </StatusListItemButtonTooltip>
    )
}

export interface INoteLinkComponentProps<TData extends IStatusListDataObject = any, TAdditional extends object = any> { 
    itemData?: IStatusListItemData<TData, TAdditional>;
    type?: NoteType;
    status?: NoteStatus;
}

export const getNoteCount = <TData extends IStatusListDataObject = any, TAdditional extends object = any>(itemData?: IStatusListItemData<TData, TAdditional>, type?: NoteType, status?:NoteStatus) => {
    switch (type) {
        case NoteType.Annotation:
            switch (status) {
                case NoteStatus.New:
                case NoteStatus.ResolutionProposed:
                case NoteStatus.Updated:
                case NoteStatus.Closed:
                    return undefined;
                case NoteStatus.NotApplicable:
                default:
                    return itemData?.status?.annotationCount ?? 0;
            }
            break;
        case NoteType.InternalQuery:
            switch (status) {
                case NoteStatus.New:
                    return itemData?.status?.internalQueryNewCount ?? 0;
                case NoteStatus.ResolutionProposed:
                    return itemData?.status?.internalQueryResolutionProposedCount ?? 0;
                case NoteStatus.Updated:
                    return itemData?.status?.internalQueryUpdatedCount ?? 0;
                case NoteStatus.Closed:
                    return itemData?.status?.internalQueryClosedCount ?? 0;
                case NoteStatus.NotApplicable:
                    return undefined;
                default:
                    return itemData?.status?.internalQueryTotalCount ?? 0;
            }
            break;
        case NoteType.ProtocolDeviation:
            switch (status) {
                case NoteStatus.New:
                case NoteStatus.ResolutionProposed:
                case NoteStatus.Updated:
                case NoteStatus.Closed:
                    return undefined;
                case NoteStatus.NotApplicable:
                default:
                    return itemData?.status?.protocolDeviationCount ?? 0;
            }
            break;
        case NoteType.ImportantProtocolDeviation:
            switch (status) {
                case NoteStatus.New:
                case NoteStatus.ResolutionProposed:
                case NoteStatus.Updated:
                case NoteStatus.Closed:
                    return undefined;
                case NoteStatus.NotApplicable:
                default:
                    return itemData?.status?.importantProtocolDeviationCount ?? 0;
            }
            break;
        case NoteType.ReasonForChange:
            switch (status) {
                case NoteStatus.New:
                case NoteStatus.ResolutionProposed:
                case NoteStatus.Updated:
                case NoteStatus.Closed:
                    return undefined;
                case NoteStatus.NotApplicable:
                    default:
                    return itemData?.status?.reasonForChangeCount ?? 0;
            }
            break;
        case NoteType.SiteQuery:
            switch (status) {
                case NoteStatus.New:
                    return itemData?.status?.siteQueryNewCount ?? 0;
                case NoteStatus.ResolutionProposed:
                    return itemData?.status?.siteQueryResolutionProposedCount ?? 0;
                case NoteStatus.Updated:
                    return itemData?.status?.siteQueryUpdatedCount ?? 0;
                case NoteStatus.Closed:
                    return itemData?.status?.siteQueryClosedCount ?? 0;
                case NoteStatus.NotApplicable:
                    return undefined;
                default:
                    return itemData?.status?.siteQueryTotalCount ?? 0;
            }
            break;
        case NoteType.System:
            switch (status) {
                case NoteStatus.New:
                    return itemData?.status?.systemQueryNewCount ?? 0;
                case NoteStatus.ResolutionProposed:
                    return itemData?.status?.systemQueryResolutionProposedCount ?? 0;
                case NoteStatus.Updated:
                    return itemData?.status?.systemQueryUpdatedCount ?? 0;
                case NoteStatus.Closed:
                    return itemData?.status?.systemQueryClosedCount ?? 0;
                case NoteStatus.NotApplicable:
                    return undefined;
                default:
                    return itemData?.status?.systemQueryTotalCount ?? 0;
            }
        default:
            switch (status) {
                case NoteStatus.New:
                    return (
                        (itemData?.status?.siteQueryNewCount ?? 0) +
                        (itemData?.status?.systemQueryNewCount ?? 0) +
                        (itemData?.status?.internalQueryNewCount ?? 0)
                    );
                case NoteStatus.ResolutionProposed:
                    return (
                        (itemData?.status?.siteQueryResolutionProposedCount ?? 0) +
                        (itemData?.status?.systemQueryResolutionProposedCount ?? 0) +
                        (itemData?.status?.internalQueryResolutionProposedCount ?? 0)
                    );
                case NoteStatus.Updated:
                    return (
                        (itemData?.status?.siteQueryUpdatedCount ?? 0) +
                        (itemData?.status?.systemQueryUpdatedCount ?? 0) +
                        (itemData?.status?.internalQueryUpdatedCount ?? 0)
                    );
                case NoteStatus.Closed:
                    return (
                        (itemData?.status?.siteQueryClosedCount ?? 0) +
                        (itemData?.status?.systemQueryClosedCount ?? 0) +
                        (itemData?.status?.internalQueryClosedCount ?? 0)
                    );
                case NoteStatus.NotApplicable:
                    return (
                        (itemData?.status?.protocolDeviationCount ?? 0) +
                        (itemData?.status?.importantProtocolDeviationCount ?? 0) +
                        (itemData?.status?.annotationCount ?? 0) +
                        (itemData?.status?.reasonForChangeCount ?? 0)
                    )
                default:
                    return (
                        (itemData?.status?.siteQueryTotalCount ?? 0) +
                        (itemData?.status?.systemQueryTotalCount ?? 0) +
                        (itemData?.status?.internalQueryTotalCount ?? 0) +
                        (itemData?.status?.protocolDeviationCount ?? 0) +
                        (itemData?.status?.importantProtocolDeviationCount ?? 0) +
                        (itemData?.status?.annotationCount ?? 0) +
                        (itemData?.status?.reasonForChangeCount ?? 0)
                    );
        }
    }
}

export const NoteLink = styled(MuiLink)(({ theme }) => ({
    color: theme.palette.common.white,
    textDecoration: 'none'
}))

export const DefaultNoteLinkComponent = memo(<TData extends IStatusListDataObject = any, TAdditional extends object = any>({
    itemData,
    status,
    type
}: INoteLinkComponentProps<TData, TAdditional>) => {
    const value = getNoteCount(itemData, type, status);

    if (value === undefined) {
        return (
            <>
                &mdash;
            </>
        )
    }

    return (
        <>
            {value}
        </>
    )
})

export const DefaultStatComponents = {
    ParticipantStats: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
        itemData, theme
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {

        if (itemData.render.disabled) {
            return (
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, undefined) }}>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.participant} /><FontAwesomeIcon size="lg" icon={AppSettings.icons.notApplicable} />
                </StatusListStatBox>
            )
        }
    
        return (
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Participants</strong>
                        <StatusListItemTooltipStats>
                            <span>
                                Total
                            </span>
                            <span>
                                {itemData.status?.participantCount ?? 0}
                            </span>
                        </StatusListItemTooltipStats>
                    </>
                }
            >
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, undefined) }}>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.participant} />{itemData.status?.participantCount ?? 0}
                </StatusListStatBox>
            </StatusListItemTooltip>
        )
    },
    FormStats: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({ 
        itemData, 
        theme 
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {
        if (itemData.render.disabled) {
            return (
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, undefined) }}>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.caseReportForm} /><FontAwesomeIcon size="lg" icon={AppSettings.icons.notApplicable} />
                </StatusListStatBox>
            )
        }
    
        return (
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Case Report Forms</strong>
                        <StatusListItemTooltipStats>
                            <span>
                                Complete
                            </span>
                            <span>
                                {itemData.status?.formsCompleteCount ?? 0}
                            </span>
                        </StatusListItemTooltipStats>
                        <StatusListItemTooltipStats>
                            <span>
                                Total
                            </span>
                            <span>
                                {itemData.status?.formsTotalCount ?? 0}
                            </span>
                        </StatusListItemTooltipStats>
                    </>
                }
            >
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, itemData.calculated.formsComplete) }}>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.caseReportForm} />{Math.floor(itemData.calculated.formsComplete).toFixed()}%
                </StatusListStatBox>
            </StatusListItemTooltip>
        )
    },
    NoteStats: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
        itemData, 
        theme
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {
        const statusColor = itemData.status?.maxNoteStatus ? AppSettings.noteStatusColors[itemData.status.maxNoteStatus] : undefined;
        if (itemData.render.disabled) {
            return (
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, undefined) }}>
                    <StyledFontAwesomeIcon 
                        sx={{
                            color: theme.palette.mode === 'dark' ? `${statusColor}.light` : `${statusColor}.dark`
                        }}
                        size="lg" 
                        icon={AppSettings.icons.note} 
                    />
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.notApplicable} />
                </StatusListStatBox>
            )
        }

        const NoteLinkComponent: ComponentType<INoteLinkComponentProps<TData, TAdditional>> = (itemData.additional as any).NoteLinkComponent ?? DefaultNoteLinkComponent;
    
        return (
            <StatusListNoteTooltip
                placement="left"
                title={
                    <>
                        <strong>Notes &amp; Queries</strong>
                        <StatusListNoteTable>
                            <thead>
                                <tr>
                                    <th>
                                        
                                    </th>
                                    <th>
                                        New
                                    </th>
                                    <th>
                                        Updated
                                    </th>
                                    <th>
                                        Resolution Proposed
                                    </th>
                                    <th>
                                        Closed
                                    </th>
                                    <th>
                                        N/A
                                    </th>
                                    <th>
                                        Total
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    AppSettings.noteTypes[NoteType.SiteQuery] && (
                                        <tr>
                                            <th>
                                                {noteTypeToText(NoteType.SiteQuery)}
                                            </th>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.New}
                                                    type={NoteType.SiteQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Updated}
                                                    type={NoteType.SiteQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.ResolutionProposed}
                                                    type={NoteType.SiteQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Closed}
                                                    type={NoteType.SiteQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.NotApplicable}
                                                    type={NoteType.SiteQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    type={NoteType.SiteQuery}
                                                />
                                            </td>
                                        </tr>
                                    )
                                }
                                {
                                    AppSettings.noteTypes[NoteType.System] && (
                                        <tr>
                                            <th>
                                                {noteTypeToText(NoteType.System)}
                                            </th>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.New}
                                                    type={NoteType.System}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Updated}
                                                    type={NoteType.System}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.ResolutionProposed}
                                                    type={NoteType.System}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Closed}
                                                    type={NoteType.System}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.NotApplicable}
                                                    type={NoteType.System}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    type={NoteType.System}
                                                />
                                            </td>
                                        </tr>
                                    )
                                }
                                {
                                    AppSettings.noteTypes[NoteType.InternalQuery] && (
                                        <tr>
                                            <th>
                                                {noteTypeToText(NoteType.InternalQuery)}
                                            </th>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.New}
                                                    type={NoteType.InternalQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Updated}
                                                    type={NoteType.InternalQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.ResolutionProposed}
                                                    type={NoteType.InternalQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Closed}
                                                    type={NoteType.InternalQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.NotApplicable}
                                                    type={NoteType.InternalQuery}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    type={NoteType.InternalQuery}
                                                />
                                            </td>
                                        </tr>
                                    )
                                }
                                
                                {
                                    AppSettings.noteTypes[NoteType.Annotation] && (
                                        <tr>
                                            <th>
                                                {noteTypeToText(NoteType.Annotation)}
                                            </th>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.New}
                                                    type={NoteType.Annotation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Updated}
                                                    type={NoteType.Annotation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.ResolutionProposed}
                                                    type={NoteType.Annotation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Closed}
                                                    type={NoteType.Annotation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.NotApplicable}
                                                    type={NoteType.Annotation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    type={NoteType.Annotation}
                                                />
                                            </td>
                                        </tr>
                                    )
                                }
                                {
                                    AppSettings.noteTypes[NoteType.ReasonForChange] && (
                                        <tr>
                                            <th>
                                                {noteTypeToText(NoteType.ReasonForChange)}
                                            </th>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.New}
                                                    type={NoteType.ReasonForChange}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Updated}
                                                    type={NoteType.ReasonForChange}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.ResolutionProposed}
                                                    type={NoteType.ReasonForChange}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Closed}
                                                    type={NoteType.ReasonForChange}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.NotApplicable}
                                                    type={NoteType.ReasonForChange}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    type={NoteType.ReasonForChange}
                                                />
                                            </td>
                                        </tr>
                                    )
                                }
                                {
                                    AppSettings.noteTypes[NoteType.ImportantProtocolDeviation] && (
                                        <tr>
                                            <th>
                                                {noteTypeToText(NoteType.ImportantProtocolDeviation)}
                                            </th>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.New}
                                                    type={NoteType.ImportantProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Updated}
                                                    type={NoteType.ImportantProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.ResolutionProposed}
                                                    type={NoteType.ImportantProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Closed}
                                                    type={NoteType.ImportantProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.NotApplicable}
                                                    type={NoteType.ImportantProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    type={NoteType.ImportantProtocolDeviation}
                                                />
                                            </td>
                                        </tr>
                                    )
                                }
                                {
                                    AppSettings.noteTypes[NoteType.ProtocolDeviation] && (
                                        <tr>
                                            <th>
                                                {noteTypeToText(NoteType.ProtocolDeviation)}
                                            </th>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.New}
                                                    type={NoteType.ProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Updated}
                                                    type={NoteType.ProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.ResolutionProposed}
                                                    type={NoteType.ProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.Closed}
                                                    type={NoteType.ProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    status={NoteStatus.NotApplicable}
                                                    type={NoteType.ProtocolDeviation}
                                                />
                                            </td>
                                            <td>
                                                <NoteLinkComponent
                                                    itemData={itemData}
                                                    type={NoteType.ProtocolDeviation}
                                                />
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th>
                                        Total
                                    </th>
                                    <th>
                                        <NoteLinkComponent
                                            itemData={itemData}
                                            status={NoteStatus.New}
                                        />
                                    </th>
                                    <th>
                                        <NoteLinkComponent
                                            itemData={itemData}
                                            status={NoteStatus.Updated}
                                        />
                                    </th>
                                    <th>
                                        <NoteLinkComponent
                                            itemData={itemData}
                                            status={NoteStatus.ResolutionProposed}
                                        />
                                    </th>
                                    <th>
                                        <NoteLinkComponent
                                            itemData={itemData}
                                            status={NoteStatus.Closed}
                                        />
                                    </th>
                                    <th>
                                        <NoteLinkComponent
                                            itemData={itemData}
                                            status={NoteStatus.NotApplicable}
                                        />
                                    </th>
                                    <th>
                                        <NoteLinkComponent
                                            itemData={itemData}
                                        />
                                    </th>
                                </tr>
                            </tfoot>
                        </StatusListNoteTable>
                    </>
                }
            >
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, itemData.calculated.queriesComplete) }}>
                    <StyledFontAwesomeIcon 
                        sx={{
                            color: theme.palette.mode === 'dark' ? `${statusColor}.light` : `${statusColor}.dark`
                        }}
                        size="lg" 
                        icon={AppSettings.icons.note} 
                    />
                    {itemData.calculated.queriesOpen}
                </StatusListStatBox>
            </StatusListNoteTooltip>
        )
    },
    ReviewStats: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({ 
        itemData, 
        theme 
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {        
        if (itemData.render.disabled) {
            return (
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, undefined) }}>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.review} /><FontAwesomeIcon size="lg" icon={AppSettings.icons.notApplicable} />
                </StatusListStatBox>
            )
        }
    
        return (
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Field Review</strong>
                        <StatusListItemTooltipStats>
                            <span>
                                Complete
                            </span>
                            <span>
                                {itemData.status?.reviewCompleteCount}
                            </span>
                        </StatusListItemTooltipStats>
                        {
                            itemData.status?.reviewNotApplicableCount && (
                                <StatusListItemTooltipStats>
                                    <span>
                                        Not Applicable
                                    </span>
                                    <span>
                                        {itemData.status?.reviewNotApplicableCount}
                                    </span>
                                </StatusListItemTooltipStats>
                            )
                        }
                        <StatusListItemTooltipStats>
                            <span>
                                Total
                            </span>
                            <span>
                                {itemData.status?.reviewTotalCount ?? 0}
                            </span>
                        </StatusListItemTooltipStats>
                    </>
                }
            >
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, itemData.calculated.reviewComplete) }}>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.review} />{Math.floor(itemData.calculated.reviewComplete).toFixed()}%
                </StatusListStatBox>
            </StatusListItemTooltip>
        )
    },
    VerificationStats: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
        itemData, 
        theme
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {
        const verificationCompleteCount = useMemo(() => (
            (itemData.status?.verificationCompleteCount ?? 0) + (itemData.status?.verificationNotApplicableCount ?? 0)
        ), [itemData.status?.verificationCompleteCount, itemData.status?.verificationNotApplicableCount]);

        if (itemData.render.disabled) {
            return (
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, undefined) }}>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.verification} /><FontAwesomeIcon size="lg" icon={AppSettings.icons.notApplicable} />
                </StatusListStatBox>
            )
        }
    
        return (
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Field Verification</strong>
                        <StatusListItemTooltipStats>
                            <span>
                                Complete
                            </span>
                            <span>
                                {itemData.status?.verificationCompleteCount}
                            </span>
                        </StatusListItemTooltipStats>
                        {
                            itemData.status?.verificationNotApplicableCount && (
                                <StatusListItemTooltipStats>
                                    <span>
                                        Not Applicable
                                    </span>
                                    <span>
                                        {itemData.status?.verificationNotApplicableCount}
                                    </span>
                                </StatusListItemTooltipStats>
                            )
                        }
                        <StatusListItemTooltipStats>
                            <span>
                                Total
                            </span>
                            <span>
                                {itemData.status?.verificationTotalCount ?? 0}
                            </span>
                        </StatusListItemTooltipStats>
                    </>
                }
            >
                <StatusListStatBox sx={{backgroundColor: getFormCompleteColor(theme, itemData.calculated.verificationComplete) }}>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.verification} />{Math.floor(itemData.calculated.verificationComplete).toFixed()}%
                </StatusListStatBox>
            </StatusListItemTooltip>
        );
    },
    DisabledIndicatorStats: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
        itemData, 
        theme
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {

        if (!itemData.render.disabled) {
            return null;
        }
    
        return (
            <StatusListItemButtonTooltip
                placement="left"
                title={
                    <>
                        <strong>Disabled</strong>
                        <div>
                            This data has been Disabled
                        </div>
                    </>
                }
            >
                <StatusListItemIconBox>
                    <FontAwesomeIcon icon={AppSettings.icons.disabled} />
                </StatusListItemIconBox>
            </StatusListItemButtonTooltip>
        );
    },
    ReadOnlyIndicatorStats: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
        itemData, 
        theme
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {

        if (itemData.data.status !== ConfigurationStatus.ReadOnly) {
            return null;
        }
    
        return (
            <StatusListItemButtonTooltip
                placement="left"
                title={
                    <>
                        <strong>Read Only</strong>
                        <div>
                            This data is set to Read Only
                        </div>
                    </>
                }
            >
                <StatusListItemIconBox>
                    <FontAwesomeIcon icon={AppSettings.icons.locked} />
                </StatusListItemIconBox>
            </StatusListItemButtonTooltip>
        );
    },
    UpdateButton: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
        itemData, 
        theme
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {
        if (!itemData.render.queueUpdate) {
            return null;
        }
    
        if (itemData.render.disabled) {
            if (itemData.render.configureLink) {
                return (
                    <StatusListConfigurationButton 
                        theme={theme}
                        itemData={itemData}
                    />
                )
            }
    
            return (
                <StatusListItemButtonBox>
                    <LoadingButton loading={false} disabled={true}>
                        <FontAwesomeIcon icon={AppSettings.icons.refresh} />
                    </LoadingButton>
                </StatusListItemButtonBox>
            )
        }
    
        return (
            <StatusListUpdateButton 
                theme={theme}
                itemData={itemData}
            />
        )
    },
    VerificationPriorityIndicatorStats: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
        itemData, 
        theme
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {
        const priority = useMemo(() => {
            

            switch (itemData.calculated.verificationPriority) {
                case SourceDataPriority.Low:
                    return {
                        icon: (
                            <StatusListItemButtonTooltip
                                placement="left"
                                title="Priority: Low"
                            >
                                <StatusListItemIconBox 
                                    sx={{ color: 'success.main' }} 
                                >
                                    <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityLow} />
                                </StatusListItemIconBox>
                            </StatusListItemButtonTooltip>
                        ),
                        color: 'success'
                    }
                case SourceDataPriority.High:
                    return {
                        icon: (
                            <StatusListItemButtonTooltip
                                placement="left"
                                title="Priority: High"
                            >
                                <StatusListItemIconBox 
                                    sx={{ color: 'warning.main' }} 
                                >
                                    <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityHigh} />
                                </StatusListItemIconBox>
                            </StatusListItemButtonTooltip>
                        ),
                        color: 'warning'
                    }
                case SourceDataPriority.Critical:
                    return {
                        icon: (
                            <StatusListItemButtonTooltip
                                placement="left"
                                title="Priority: Critical"
                            >
                                <StatusListItemIconBox 
                                    sx={{ color: 'error.main' }} 
                                >
                                    <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityCritical} />
                                </StatusListItemIconBox>
                            </StatusListItemButtonTooltip>
                        ),
                        color: 'error'
                    }
                case SourceDataPriority.Normal:
                    return {
                        icon: (
                            <StatusListItemButtonTooltip
                                placement="left"
                                title="Priority: Normal"
                            >
                                <StatusListItemIconBox>
                                    <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityNormal} />
                                </StatusListItemIconBox>
                            </StatusListItemButtonTooltip>
                        ),
                        color: undefined
                    }
            }

            return {
                icon: null,
                color: undefined
            }
        }, [itemData.calculated.verificationPriority]);

        return (
            priority.icon
        );
    },
    ReviewPriorityIndicatorStats: <TData extends IStatusListDataObject = any, TAdditional extends object = any>({
        itemData, 
        theme
    }: IStatusListStatsComponentProps<TData, TAdditional>) => {
        const priority = useMemo(() => {
            

            switch (itemData.calculated.reviewPriority) {
                case SourceDataPriority.Low:
                    return {
                        icon: (
                            <StatusListItemButtonTooltip
                                placement="left"
                                title="Priority: Low"
                            >
                                <StatusListItemIconBox 
                                    sx={{ color: 'success.main' }} 
                                >
                                    <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityLow} />
                                </StatusListItemIconBox>
                            </StatusListItemButtonTooltip>
                        ),
                        color: 'success'
                    }
                case SourceDataPriority.High:
                    return {
                        icon: (
                            <StatusListItemButtonTooltip
                                placement="left"
                                title="Priority: High"
                            >
                                <StatusListItemIconBox 
                                    sx={{ color: 'warning.main' }} 
                                >
                                    <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityHigh} />
                                </StatusListItemIconBox>
                            </StatusListItemButtonTooltip>
                        ),
                        color: 'warning'
                    }
                case SourceDataPriority.Critical:
                    return {
                        icon: (
                            <StatusListItemButtonTooltip
                                placement="left"
                                title="Priority: Critical"
                            >
                                <StatusListItemIconBox 
                                    sx={{ color: 'error.main' }} 
                                >
                                    <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityCritical} />
                                </StatusListItemIconBox>
                            </StatusListItemButtonTooltip>
                        ),
                        color: 'error'
                    }
                case SourceDataPriority.Normal:
                    return {
                        icon: (
                            <StatusListItemButtonTooltip
                                placement="left"
                                title="Priority: Normal"
                            >
                                <StatusListItemIconBox>
                                    <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityNormal} />
                                </StatusListItemIconBox>
                            </StatusListItemButtonTooltip>
                        ),
                        color: undefined
                    }
            }

            return {
                icon: null,
                color: undefined
            }
        }, [itemData.calculated.reviewPriority]);
    
        return (
            priority.icon
        );
    },
}

export default DefaultStatComponents;